import { resolveCatalogCacheTtlMs } from "../cache/modelCache.js";
import { loadCatalogSources, type LoadCatalogOptions, type LoadedCatalogs } from "./loadCatalogSources.js";

type CacheSlot = {
  loadedAt: number;
  data: LoadedCatalogs;
};

const slots = new Map<string, CacheSlot>();
const inflight = new Map<string, Promise<LoadedCatalogs>>();

function slotKey(options: LoadCatalogOptions, cacheKey: string): string {
  return [
    cacheKey,
    options.bundledOnly ? "bundled" : (options.source ?? "auto"),
  ].join("\0");
}

export function invalidateCatalogLoadCache(cacheKey = "default"): void {
  for (const key of [...slots.keys()]) {
    if (key.startsWith(`${cacheKey}\0`)) slots.delete(key);
  }
  for (const key of [...inflight.keys()]) {
    if (key.startsWith(`${cacheKey}\0`)) inflight.delete(key);
  }
}

export type LoadCatalogCachedOptions = {
  cacheKey?: string;
  ttlMs?: number;
  /** Bypass cache and fetch again (e.g. `catalog refresh`). */
  forceRefresh?: boolean;
};

/**
 * Load catalogs with a process-wide in-memory cache.
 * Loads run at most once per TTL window (default 24h); concurrent callers share one in-flight request.
 */
export async function loadCatalogSourcesCached(
  options: LoadCatalogOptions = {},
  cacheOptions: LoadCatalogCachedOptions = {},
): Promise<LoadedCatalogs> {
  const ttlMs = resolveCatalogCacheTtlMs(cacheOptions.ttlMs);
  const key = slotKey(options, cacheOptions.cacheKey ?? "default");

  if (!cacheOptions.forceRefresh) {
    const slot = slots.get(key);
    if (slot && Date.now() - slot.loadedAt < ttlMs) {
      return slot.data;
    }
  } else {
    slots.delete(key);
    inflight.delete(key);
  }

  const pending = inflight.get(key);
  if (pending) return pending;

  const promise = loadCatalogSources(options)
    .then((data) => {
      slots.set(key, { loadedAt: Date.now(), data });
      inflight.delete(key);
      return data;
    })
    .catch((err) => {
      inflight.delete(key);
      throw err;
    });

  inflight.set(key, promise);
  return promise;
}
